import { Check, X } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { DisabledHintTooltip } from '@ooh/ui/components/disabled-hint-tooltip';
import { fmtPln } from './client-offer-review-format';
import { ClientOfferReviewExpandPhotoBlock } from './client-offer-review-photo-blocks';
import { ClientOfferReviewPeriodChips } from './client-offer-review-period-chips';
import type {
  ClientOfferReviewDecision,
  ClientOfferReviewPeriod,
  ClientOfferReviewUnit,
  ClientOfferReviewUnitCardLabels,
} from './client-offer-review-types';
import { unitDecisionButtonClassName } from './client-offer-review-unit-card-model';
import type { StopFn } from './client-offer-review-unit-card';

/**
 * Accept-button title/aria text for the current decision + read-only/locked/accept-disabled
 * state. Precedence: `readOnly` (preview mode) > `locked` (carried forward, binding) >
 * `acceptDisabled` (zero pickable periods) — bug fix: silent accept failure for units with
 * zero pickable periods.
 */
function acceptActionText(
  readOnly: boolean,
  roHint: string,
  locked: boolean,
  lockedHint: string,
  acceptDisabled: boolean,
  decision: ClientOfferReviewDecision,
  labels: ClientOfferReviewUnitCardLabels,
) {
  if (readOnly) return roHint;
  if (locked) return lockedHint;
  if (acceptDisabled) return labels.acceptDisabledHint;
  return decision === 'accepted' ? labels.undoAcceptAction : labels.acceptAction;
}

/** Reject-button title/aria text for the current decision + read-only/locked state. */
function rejectActionText(
  readOnly: boolean,
  roHint: string,
  locked: boolean,
  lockedHint: string,
  decision: ClientOfferReviewDecision,
  labels: ClientOfferReviewUnitCardLabels,
) {
  if (readOnly) return roHint;
  if (locked) return lockedHint;
  return decision === 'rejected' ? labels.undoRejectAction : labels.rejectAction;
}

/** Formats a price-breakdown value, falling back to "do potwierdzenia" for `null` (unconfirmed/missing). */
function fmtPlnOrPending(value: number | null, pendingLabel: string): string {
  return fmtPln(value) ?? pendingLabel;
}

interface DecisionControlsState {
  locked: boolean;
  acceptText: string;
  rejectText: string;
  effectiveAcceptDisabled: boolean;
  effectiveRejectDisabled: boolean;
  acceptHint: string;
  rejectHint: string;
}

/**
 * Derives every accept/reject control's disabled/text/tooltip state in one place — extracted
 * from UnitCardExpandedPanel to keep that component's cyclomatic complexity within the lint
 * budget once the `locked` (offer-decision-carry-forward) branching was added alongside the
 * pre-existing `readOnly`/`acceptDisabled` branching. A plain sold unit (no carry-forward
 * pre-fill) shares the same disabled+lockedHint treatment as a carried-forward locked unit —
 * both are binding decisions the client can no longer change from this view.
 */
function computeDecisionControlsState(
  unit: ClientOfferReviewUnit,
  decision: ClientOfferReviewDecision,
  readOnly: boolean,
  roHint: string,
  labels: ClientOfferReviewUnitCardLabels,
): DecisionControlsState {
  const locked = unit.locked === true || unit.sold;
  // An already-accepted unit is never gated by canAccept — its pickable periods may
  // have dropped to zero after it was accepted (carried forward, or accepted earlier
  // this session), and the client must always be able to undo the acceptance, not get
  // stuck with a fully-disabled button (bug fix — PR #920 review finding). A locked unit
  // is accepted-and-binding by construction, so this never applies to it in practice.
  const acceptDisabled = unit.canAccept === false && decision !== 'accepted';
  return {
    locked,
    acceptText: acceptActionText(
      readOnly,
      roHint,
      locked,
      labels.lockedHint,
      acceptDisabled,
      decision,
      labels,
    ),
    rejectText: rejectActionText(readOnly, roHint, locked, labels.lockedHint, decision, labels),
    effectiveAcceptDisabled: readOnly || locked || acceptDisabled,
    effectiveRejectDisabled: readOnly || locked,
    acceptHint: readOnly ? roHint : locked ? labels.lockedHint : labels.acceptDisabledHint,
    // readOnly (preview mode) wins first, matching acceptHint/acceptText/rejectText's own
    // readOnly > locked precedence — a unit that is both readOnly and locked must show one
    // consistent explanation, not the locked hint on hover contradicting the preview hint
    // on the native title/aria-label (bug fix).
    rejectHint: readOnly ? roHint : locked ? labels.lockedHint : roHint,
  };
}

interface UnitCardExpandedPanelProps {
  unit: ClientOfferReviewUnit;
  photoMode: 'large' | 'small' | 'none';
  showKlocki: boolean;
  periods: ClientOfferReviewPeriod[];
  selectedPeriodIds: string[];
  decision: ClientOfferReviewDecision;
  readOnly: boolean;
  roHint: string;
  grayFilter: boolean;
  onOpenDetail: () => void;
  onOpenLightbox: () => void;
  onAccept: () => void;
  onReject: () => void;
  /** Toggles a free chip on this unit's period strip — see ClientOfferReviewPeriodChips gating. */
  onTogglePeriod?: (periodId: string, nowSelected: boolean) => void;
  labels: ClientOfferReviewUnitCardLabels;
  stop: StopFn;
}

/** Always-visible detail panel: none-mode photo block, GRW/supplier line, price breakdown, read-only period chips, single consolidated action row (accept/reject/"Zobacz szczegóły"). */
export function UnitCardExpandedPanel({
  unit,
  photoMode,
  showKlocki,
  periods,
  selectedPeriodIds,
  decision,
  readOnly,
  roHint,
  grayFilter,
  onOpenDetail,
  onOpenLightbox,
  onAccept,
  onReject,
  onTogglePeriod,
  labels,
  stop,
}: UnitCardExpandedPanelProps) {
  const klockiHint = labels.periodChips.selectedRangeSummary(
    selectedPeriodIds.length,
    periods.length,
  );
  const {
    acceptText,
    rejectText,
    effectiveAcceptDisabled,
    effectiveRejectDisabled,
    acceptHint,
    rejectHint,
  } = computeDecisionControlsState(unit, decision, readOnly, roHint, labels);

  return (
    <div
      data-testid={`expand-panel-${unit.id}`}
      className={`flex flex-col gap-2 border-t border-border pt-2 ${grayFilter ? 'grayscale opacity-50' : ''}`}
    >
      {/*
       * Just "Nr {GRW code}" — client's target mockup drops supplier
       * number/name and voivodeship from this line entirely (voivodeship
       * already shows inline in the header's address line). It always
       * sits directly under the header (before the none-mode photo
       * block) — client feedback was specific that the number belongs
       * immediately under the header, not pushed down by the photo
       * thumbnail. Weekly/monthly audience + GRP were both crossed out
       * entirely and dropped from this card (no longer shown here or in
       * the header row).
       */}
      <div
        className="text-xs text-muted-foreground"
        style={{ fontFamily: 'ui-monospace, monospace' }}
      >
        {`${labels.grwNumberLabel} ${unit.grw}`}
      </div>
      {photoMode === 'none' ? (
        <ClientOfferReviewExpandPhotoBlock
          photoUrl={unit.thumbnailUrl}
          onOpen={stop(onOpenLightbox)}
          labels={labels.photoBlock}
        />
      ) : null}
      {/*
       * Price breakdown — always visible instead of hidden behind "Zobacz
       * szczegóły". Shows exactly one row: the agreed net price after any
       * discount (`unit.price`, the same value already shown in the header
       * row). The client never sees the pre-discount catalog price — CU-86cb4wkxx.
       * Produkcja/Montaż rows are deferred to REQ-7.32 — no client-safe
       * snapshot data source exists yet (see design.md Decision 4).
       */}
      <div className="flex flex-wrap gap-x-3.5 gap-y-1 text-xs text-muted-foreground">
        <span>
          {labels.discountedPriceLabel}:{' '}
          <b className="font-semibold text-foreground">
            {fmtPlnOrPending(unit.price, labels.priceConfirmationPending)}
          </b>
        </span>
      </div>
      {showKlocki ? (
        <ClientOfferReviewPeriodChips
          unit={unit}
          periods={periods}
          selectedIds={new Set(selectedPeriodIds)}
          hint={klockiHint}
          labels={labels.periodChips}
          decision={decision}
          locked={unit.locked === true}
          lockedHint={labels.lockedHint}
          readOnly={readOnly}
          roHint={roHint}
          onTogglePeriod={onTogglePeriod}
        />
      ) : null}
      {/*
       * Client feedback: accept/reject moved down here from the
       * header row into one consolidated action row alongside "Zobacz
       * szczegóły" (client-offer-review-unit-card-header-row.tsx now only
       * shows identity + price). The header's separate "(i)" details icon
       * was removed entirely — this button is the single path to the
       * detail modal. The "Otwórz w Google Maps" link was dropped here
       * specifically — it still exists inside the detail modal itself
       * (client-offer-review-detail-modal.tsx), which the client asked to
       * keep.
       */}
      <div className="flex items-center gap-2">
        {/* Stop propagation on the wrapper itself, not just the Button's own `stop(...)`
         * handler: a disabled Button has `pointer-events: none`, so a click never reaches
         * it — it lands on DisabledHintTooltip's `span[tabIndex=0]` wrapper instead, which
         * has no handler of its own and would otherwise bubble straight to the card's
         * `onClick={onLocate}` (CU-86cbc55rv review finding). */}
        <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
          <DisabledHintTooltip disabled={effectiveAcceptDisabled} hint={acceptHint}>
            <Button
              type="button"
              variant="outline"
              size="icon-md"
              onClick={stop(onAccept)}
              disabled={effectiveAcceptDisabled}
              title={acceptText}
              aria-label={acceptText}
              data-testid={`accept-${unit.id}`}
              className={`rounded-lg ${unitDecisionButtonClassName(decision === 'accepted', 'accept')}`}
            >
              <Check className="h-4 w-4" strokeWidth={2.4} />
            </Button>
          </DisabledHintTooltip>
          <DisabledHintTooltip disabled={effectiveRejectDisabled} hint={rejectHint}>
            <Button
              type="button"
              variant="outline"
              size="icon-md"
              onClick={stop(onReject)}
              disabled={effectiveRejectDisabled}
              title={rejectText}
              aria-label={rejectText}
              data-testid={`reject-${unit.id}`}
              className={`rounded-lg ${unitDecisionButtonClassName(decision === 'rejected', 'reject')}`}
            >
              <X className="h-3.5 w-3.5" strokeWidth={2.4} />
            </Button>
          </DisabledHintTooltip>
        </div>
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={stop(onOpenDetail)}
          className="h-7 rounded-lg border-border text-xs font-medium"
        >
          {labels.seeDetails}
        </Button>
      </div>
    </div>
  );
}
